import { useState } from "react";
import {
  HiArrowLeft,
  HiArrowRight,
  HiExternalLink,
  HiCode,
  HiViewGrid,
} from "react-icons/hi";
import BankPortalImage from "../assets/project-screenshots/BankPortfolioLMIS.webp";
import BexChat from "../assets/project-screenshots/bex-chat.JPG";
import Robibon from "../assets/project-screenshots/robibon.JPG";
import GPACalc from "../assets/project-screenshots/ReactGPACalc.jpg";
import TaskTracker from "../assets/project-screenshots/ReactProjectTaskTracker.png";
import DiscordClone from "../assets/project-screenshots/DiscordClone.PNG";
import TodoListVanilla from "../assets/project-screenshots/Todolist.PNG";
import ImageViewer from "./ImageViewer";

const Projects = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageViewer, setImageViewer] = useState({
    isOpen: false,
    src: "",
    alt: "",
  });

  const projects = [
    {
      id: 1,
      title: "E-LMIS: Commission Enforcement Bank Portal",
      description:
        "Production-grade financial reporting portal for regulated environments. Built with deterministic state handling, multi-layer submission locks, and performance-first architecture.",
      image: BankPortalImage,
      technologies: [
        "React 19",
        "TypeScript 5.8",
        "Apollo GraphQL",
        "Tailwind CSS 4.0",
        "Vite 6",
      ],
      highlights: [
        "308.83 kB ultralight payload for entire initial flow (Login, 2FA, Dashboard)",
        "4-level defensive submission pipeline preventing duplicate financial entries",
        "Zero critical issues in production since launch - QA team's highest rating",
        "Custom PDF generation with responsive layouts across viewports",
      ],
      liveUrl: null,
      codeUrl: null,
      type: "Professional",
    },
    {
      id: 2,
      title: "E-LMIS - Expatriate Work Permit System",
      description:
        "National-level platform for managing work permits and labour data in Ethiopia. Leading the development of frontend and backend modules with complex workflows and data management.",
      image: null,
      technologies: [
        "React",
        "NestJS",
        "Hasura GraphQL",
        "PostgreSQL",
        "TypeScript",
      ],
      highlights: [
        "Developed frontend and backend modules for work-permit workflows",
        "Integrated GraphQL & REST APIs for performance and scalability",
        "Designed reusable components optimized for data-heavy operations",
        "Collaborated on architecture and CI/CD deployment processes",
      ],
      liveUrl: null,
      codeUrl: null,
      type: "Professional",
    },
    {
      id: 3,
      title: "BexChat - Real-time Chat Application",
      description:
        "Modern real-time chat application built with React and GraphQL. Features include instant messaging, user authentication, and responsive design.",
      image: BexChat,
      technologies: ["React", "GraphQL", "Node.js", "WebSockets"],
      highlights: [
        "Real-time messaging with GraphQL subscriptions",
        "User authentication and authorization",
        "Responsive design with modern UI/UX",
        "Optimized performance for instant communication",
      ],
      liveUrl: "https://bex-chat.vercel.app",
      codeUrl: null,
      type: "Personal",
    },
    {
      id: 4,
      title: "ROBIBON Construction Website",
      description:
        "Complete web presence and brand identity for construction company. Custom-built responsive website with SEO optimization and professional design.",
      image: Robibon,
      technologies: ["HTML", "CSS/SCSS", "JavaScript", "SEO"],
      highlights: [
        "Designed and deployed custom responsive website",
        "Delivered complete brand identity materials",
        "Managed hosting, maintenance, and SEO",
        "Technical consulting for digital transformation",
      ],
      liveUrl: "https://www.robibonconstruction.com/",
      codeUrl: null,
      type: "Client Work",
    },
    {
      id: 5,
      title: "AI-Based Smart Traffic Management System",
      description:
        "Award-winning final year project featuring density-based traffic control with real-time and predictive modes to optimize intersection flow and reduce congestion.",
      image: null,
      technologies: ["Arduino", "TensorFlow", "Python", "C++"],
      highlights: [
        "1st Place - Final Year Project Award",
        "Designed microcontroller logic and sensor-based algorithms",
        "Implemented both real-time and predictive traffic control modes",
        "Reduced congestion through intelligent traffic light optimization",
      ],
      liveUrl: null,
      codeUrl: null,
      type: "Academic",
    },
    {
      id: 6,
      title: "React Task Tracker - MERN Stack",
      description:
        "Full-stack task management application built with MERN stack. Features include task creation, editing, and status tracking with a clean, intuitive interface.",
      image: TaskTracker,
      technologies: ["React", "Node.js", "Express", "MongoDB"],
      highlights: [
        "Full CRUD functionality for task management",
        "RESTful API design with Express",
        "MongoDB database integration",
        "Responsive React interface",
      ],
      liveUrl: null,
      codeUrl: "https://github.com/Beki-D/react-task-tracker",
      type: "Personal",
    },
    {
      id: 7,
      title: "Discord Clone Dashboard",
      description:
        "Modern recreation of Discord's dashboard interface using React and TailwindCSS. Demonstrates advanced UI/UX implementation and responsive design patterns.",
      image: DiscordClone,
      technologies: ["React", "TailwindCSS", "JavaScript"],
      highlights: [
        "Pixel-perfect UI recreation",
        "Advanced responsive design techniques",
        "Component-based architecture",
        "Modern styling with TailwindCSS",
      ],
      liveUrl: null,
      codeUrl: "https://github.com/Beki-D/discord-dashboard-clone-tw",
      type: "Personal",
    },
    {
      id: 8,
      title: "GPA Calculator - React Application",
      description:
        "Academic GPA calculation tool built with React. Features semester management, grade tracking, and cumulative GPA calculation with an intuitive interface.",
      image: GPACalc,
      technologies: ["React", "JavaScript", "CSS"],
      highlights: [
        "Semester-based GPA calculation",
        "Grade point management system",
        "Cumulative GPA tracking",
        "User-friendly interface design",
      ],
      liveUrl: null,
      codeUrl: null,
      type: "Personal",
    },
    {
      id: 9,
      title: "Advanced Todo Application",
      description:
        "Feature-rich todo list application built with vanilla JavaScript. Demonstrates core JavaScript skills with local storage, filtering, and task categorization.",
      image: TodoListVanilla,
      technologies: ["JavaScript", "HTML", "CSS", "Local Storage"],
      highlights: [
        "Pure vanilla JavaScript implementation",
        "Local storage for data persistence",
        "Advanced filtering and categorization",
        "Clean, modern interface",
      ],
      liveUrl: "https://my-advanced-todo-app.vercel.app/",
      codeUrl: "https://github.com/Beki-D/My-advanced-todo-app",
      type: "Personal",
    },
  ];

  const openImageViewer = (src, alt) => {
    setImageViewer({ isOpen: true, src, alt });
  };

  const closeImageViewer = () => {
    setImageViewer({ isOpen: false, src: "", alt: "" });
  };

  const nextProject = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === projects.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevProject = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? projects.length - 1 : prevIndex - 1
    );
  };

  const currentProject = projects[currentIndex];

  const getTypeColor = (type) => {
    switch (type) {
      case "Professional":
        return "bg-green-600 text-white";
      case "Client Work":
        return "bg-blue-600 text-white";
      case "Academic":
        return "bg-yellow-600 text-white";
      default:
        return "bg-cyan-600 text-white";
    }
  };

  return (
    <div
      name="work"
      className="w-full min-h-screen bg-[#0a192f] text-gray-300 py-20"
    >
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="pb-12">
          <h2 className="text-4xl sm:text-5xl font-bold inline-block border-b-4 border-cyan-600">
            Projects & Work
          </h2>
          <p className="text-[#8892b0] text-lg mt-4">
            Showcasing professional projects, client work, and personal
            developments
          </p>
        </div>

        <div className="relative">
          <div className="bg-[#112240] rounded-2xl border border-cyan-600 border-opacity-20 overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8 p-8 lg:p-12">
              <div className="flex flex-col justify-between space-y-6">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span
                      className={`px-4 py-1 rounded-full text-sm font-semibold ${getTypeColor(
                        currentProject.type
                      )}`}
                    >
                      {currentProject.type}
                    </span>
                    <span className="text-[#8892b0] text-sm">
                      {currentIndex + 1} / {projects.length}
                    </span>
                  </div>

                  <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                    {currentProject.title}
                  </h3>

                  <p className="text-[#8892b0] text-lg leading-relaxed mb-6">
                    {currentProject.description}
                  </p>

                  <div className="space-y-3 mb-6">
                    {currentProject.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <span className="text-cyan-500 mt-1">▹</span>
                        <p className="text-[#8892b0]">{highlight}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {currentProject.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-cyan-600 bg-opacity-10 text-cyan-400 rounded-md text-sm border border-cyan-600 border-opacity-20"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4">
                  {currentProject.liveUrl && (
                    <a
                      href={currentProject.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-all duration-300 font-semibold"
                    >
                      <HiExternalLink className="text-xl" />
                      Live Demo
                    </a>
                  )}
                  {currentProject.codeUrl && (
                    <a
                      href={currentProject.codeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-6 py-3 border-2 border-cyan-600 text-cyan-400 rounded-lg hover:bg-cyan-600 hover:text-white transition-all duration-300 font-semibold"
                    >
                      <HiCode className="text-xl" />
                      View Code
                    </a>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-center">
                {currentProject.image ? (
                  <div className="relative group">
                    <img
                      src={currentProject.image}
                      alt={currentProject.title}
                      className="rounded-lg shadow-2xl max-h-[500px] object-cover border border-cyan-600 border-opacity-30 hover:border-opacity-50 transition-all duration-300"
                    />
                    <button
                      onClick={() =>
                        openImageViewer(
                          currentProject.image,
                          currentProject.title
                        )
                      }
                      className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-opacity-70"
                    >
                      <HiViewGrid className="text-lg" />
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-[400px] bg-[#0a192f] rounded-lg flex items-center justify-center border border-cyan-600 border-opacity-30">
                    <div className="text-center space-y-3">
                      <HiCode className="text-6xl text-cyan-500 mx-auto" />
                      <p className="text-[#8892b0] text-lg">
                        Project In Production
                      </p>
                      <p className="text-[#8892b0] text-sm">
                        Visuals Coming Soon
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={prevProject}
              className="p-4 bg-[#112240] text-cyan-400 rounded-full border-2 border-cyan-600 border-opacity-30 hover:border-opacity-100 hover:bg-cyan-600 hover:text-white transition-all duration-300"
            >
              <HiArrowLeft className="text-2xl" />
            </button>
            <button
              onClick={nextProject}
              className="p-4 bg-[#112240] text-cyan-400 rounded-full border-2 border-cyan-600 border-opacity-30 hover:border-opacity-100 hover:bg-cyan-600 hover:text-white transition-all duration-300"
            >
              <HiArrowRight className="text-2xl" />
            </button>
          </div>

          <div className="flex justify-center gap-2 mt-6">
            {projects.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === currentIndex
                    ? "w-8 bg-cyan-600"
                    : "w-2 bg-[#8892b0] hover:bg-cyan-600"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Image Viewer */}
        <ImageViewer
          src={imageViewer.src}
          alt={imageViewer.alt}
          isOpen={imageViewer.isOpen}
          onClose={closeImageViewer}
        />
      </div>
    </div>
  );
};

export default Projects;
